import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PaperPlaneTilt, Phone, EnvelopeSimple, MapPin, CircleNotch } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/40 transition-[border-color,box-shadow] duration-200";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.toastValidation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.toastSuccess);
      setForm(initial);
    } catch (err) {
      toast.error(c.toastError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative bg-[#1e2a6e] py-24 sm:py-32 overflow-hidden grain">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="font-accent text-xs tracking-[0.2em] uppercase font-bold text-[#4a90d9]">
            {c.overline}
          </span>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight text-white mt-4">
            {c.title}
          </h2>
          <p className="text-white/75 mt-5 text-base sm:text-lg leading-relaxed max-w-md">{c.text}</p>

          <div className="mt-10 space-y-5">
            <a href="mailto:info@jasupport.de" data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#4a90d9] transition-colors duration-200">
                <EnvelopeSimple size={22} weight="duotone" className="text-white" />
              </span>
              <span className="text-white/90 group-hover:text-white transition-colors">info@jasupport.de</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <Phone size={22} weight="duotone" className="text-white" />
              </span>
              <span className="text-white/90">{c.phoneLabel}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <MapPin size={22} weight="duotone" className="text-white" />
              </span>
              <span className="text-white/90">{c.locationLabel}</span>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white/5 border border-white/15 backdrop-blur-sm p-6 sm:p-8 space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-input-name" name="name" value={form.name} onChange={onChange} placeholder={c.form.name} className={inputCls} />
            <input data-testid="contact-input-email" name="email" type="email" value={form.email} onChange={onChange} placeholder={c.form.email} className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-input-phone" name="phone" value={form.phone} onChange={onChange} placeholder={c.form.phone} className={inputCls} />
            <input data-testid="contact-input-subject" name="subject" value={form.subject} onChange={onChange} placeholder={c.form.subject} className={inputCls} />
          </div>
          <textarea data-testid="contact-input-message" name="message" value={form.message} onChange={onChange} placeholder={c.form.message} rows={5} className={`${inputCls} resize-none`} />
          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4a90d9] px-8 py-4 text-base font-semibold text-white hover:bg-white hover:text-[#1e2a6e] transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? (
              <>
                <CircleNotch size={20} weight="bold" className="animate-spin" /> {c.form.sending}
              </>
            ) : (
              <>
                {c.form.submit} <PaperPlaneTilt size={20} weight="fill" />
              </>
            )}
          </button>
          <p className="text-xs text-white/50 text-center">{c.form.required}</p>
        </motion.form>
      </div>
    </section>
  );
}
